import React from 'react';
import { Box, Typography } from '@mui/material';
import { BookOpen } from 'lucide-react';

/**
 * תבנית עמודים — כל קטגוריה כעמוד נפרד בפריסה אופקית.
 * עמודות זו לצד זו כמו עמודי בית הכנסת, קרם חם עם פסי זהב.
 */

const NAVY = '#1e3a5f';
const CREAM = '#faf8f4';
const GOLD = '#C9A227';
const font = 'Frank Ruhl Libre, David, serif';

const CATEGORY_ACCENT = {
  gemara: '#1e3a5f', halacha: '#9a7209', tanakh: '#2d5016',
  mishna: '#7c3aed', mussar: '#b45309',
};

/* ── עמוד קטגוריה ── */
const CategoryColumn = ({ group }) => {
  const accent = CATEGORY_ACCENT[group.category] || NAVY;

  return (
    <Box sx={{
      flex: '1 1 0', minWidth: 120, maxWidth: 200,
      display: 'flex', flexDirection: 'column',
      bgcolor: 'white', borderRadius: '8px',
      border: '1px solid #e8e0d0',
      overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    }}>
      {/* כותרת עמוד */}
      <Box sx={{
        bgcolor: accent, py: 0.6, px: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
      }}>
        <Typography sx={{
          fontFamily: font, fontSize: '0.58rem', fontWeight: 700,
          color: 'white', letterSpacing: '0.06em',
        }}>
          {group.label}
        </Typography>
      </Box>

      {/* לימודים */}
      <Box sx={{ p: 1, flex: 1 }}>
        {group.items.map((item, i) => (
          <Box key={item.key || i} sx={{
            py: 0.6,
            borderBottom: i < group.items.length - 1 ? '1px solid #f0ece4' : 'none',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
              <Typography sx={{
                fontFamily: font, fontSize: '0.6rem', fontWeight: 700,
                color: accent, lineHeight: 1.3,
              }}>
                {item.labelHe}
              </Typography>
              {item.url && (
                <BookOpen size={8} style={{ color: GOLD, opacity: 0.45, flexShrink: 0 }} />
              )}
            </Box>
            <Typography sx={{
              fontFamily: font, fontSize: '0.56rem', fontWeight: 500,
              color: '#4b5563', lineHeight: 1.3, mt: 0.15,
            }}>
              {item.valueHe}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/* ── Flat fallback column ── */
const FlatColumn = ({ items }) => (
  <Box sx={{
    flex: '1 1 0', minWidth: 120,
    display: 'flex', flexDirection: 'column',
    bgcolor: 'white', borderRadius: '8px',
    border: '1px solid #e8e0d0',
    overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  }}>
    <Box sx={{ bgcolor: NAVY, py: 0.6, px: 1, textAlign: 'center' }}>
      <Typography sx={{ fontFamily: font, fontSize: '0.58rem', fontWeight: 700, color: 'white' }}>
        לימודים יומיים
      </Typography>
    </Box>
    <Box sx={{ p: 1 }}>
      {items.map((item, i) => (
        <Box key={item.key || i} sx={{
          py: 0.5,
          borderBottom: i < items.length - 1 ? '1px solid #f0ece4' : 'none',
        }}>
          <Typography sx={{ fontFamily: font, fontSize: '0.6rem', fontWeight: 700, color: NAVY }}>
            {item.labelHe}
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: '0.54rem', fontWeight: 500, color: '#4b5563' }}>
            {item.valueHe}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

/* ══ Columns Template ══ */
const Columns = ({ today, items, groups, loading, error, title, showTitle }) => {
  const hebrewDate = today?.hebrewDate?.rendered || '';
  const displayGroups = groups || [];
  const flatItems = !groups && Array.isArray(items) ? items : null;

  return (
    <Box sx={{
      width: '100%', height: '100%',
      bgcolor: CREAM, direction: 'rtl', fontFamily: font,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <Box sx={{
        flex: 1, overflow: 'auto', p: 1.5,
        '&::-webkit-scrollbar': { width: 2, height: 2 },
        '&::-webkit-scrollbar-thumb': { background: '#d4c9b0', borderRadius: 1 },
      }}>
        {/* כותרת */}
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          {showTitle && (
            <Typography sx={{
              fontFamily: font, fontSize: '0.78rem', fontWeight: 700,
              color: NAVY, letterSpacing: '0.08em',
            }}>
              {title}
            </Typography>
          )}
          {hebrewDate && (
            <Typography sx={{
              fontFamily: font, fontSize: '0.58rem', fontWeight: 500,
              color: '#9a7209',
            }}>
              {hebrewDate}
            </Typography>
          )}
          <Box sx={{
            height: '1px', mt: 0.5, mx: 'auto', maxWidth: '40%',
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
            opacity: 0.3,
          }} />
        </Box>

        {/* Loading / Error / Empty */}
        {loading && (
          <Typography sx={{ fontFamily: font, fontSize: '0.6rem', color: '#94a3b8', textAlign: 'center' }}>
            טוען...
          </Typography>
        )}
        {error && (
          <Typography sx={{ fontSize: '0.6rem', color: '#ef4444', textAlign: 'center' }}>
            שגיאה בטעינה
          </Typography>
        )}
        {displayGroups.length === 0 && !flatItems && !loading && (
          <Typography sx={{ fontFamily: font, fontSize: '0.6rem', color: '#94a3b8', textAlign: 'center', mt: 2 }}>
            אין לימודים להצגה
          </Typography>
        )}

        {/* עמודות — פריסה אופקית */}
        {displayGroups.length > 0 && (
          <Box sx={{
            display: 'flex', flexWrap: 'wrap',
            gap: 1, justifyContent: 'center',
          }}>
            {displayGroups.map((group) => (
              <CategoryColumn key={group.category} group={group} />
            ))}
          </Box>
        )}

        {/* Flat fallback for preview */}
        {flatItems && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <FlatColumn items={flatItems} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Columns;
